import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import Navbar from "./Navbar";
import { db, auth } from "./firebase";
import { useLocation, useNavigate } from "react-router-dom";
import { onAuthStateChanged, sendSignInLinkToEmail } from "firebase/auth";
import {
  collection,
  query,
  where,
  onSnapshot,
  getDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  arrayRemove,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "./components/Spinner";
import LandingPage from "./components/LandingPage";
import ProductForm from "./components/ProductForm";
import AlertDashboard from "./components/AlertDashboard";
import NewsletterInvite from "./components/NewsletterInvite";
import SocialLinks from "./components/SocialLinks";
import CookiesBanner from "./components/CookiesBanner";
import Footer from "./components/Footer";

const FREE_SUBSCRIPTION_LIMIT = parseInt(process.env.REACT_APP_FREE_SUBSCRIPTION_LIMIT) || 5;

const actionCodeSettings = {
  url: "https://www.avisamesibaja.com/confirm-email",
  handleCodeInApp: true,
};

function appendAffiliateTag(originalUrl) {
  const AMAZON_TRACKING_IDS = {
    'amazon.com': 'avisamesiba04-20',
    'www.amazon.com': 'avisamesiba04-20',
    'amazon.es': 'avisamesiba0e-21',
    'www.amazon.es': 'avisamesiba0e-21',
  };
  try {
    const url = new URL(originalUrl);
    const domain = url.hostname.replace(/^www\./, '');
    url.searchParams.set("tag", AMAZON_TRACKING_IDS[domain] || 'avisamesiba0e-21');
    return url.toString();
  } catch (err) {
    console.error("URL inválida al añadir tag:", originalUrl);
    return originalUrl;
  }
}

async function getFreePlanLimit() {
  try {
    const freePlanDoc = await getDoc(doc(db, "plans", "FREE"));
    if (freePlanDoc.exists()) {
      const data = freePlanDoc.data();
      return data.limit || FREE_SUBSCRIPTION_LIMIT;
    }
  } catch (error) {
    console.error("Error al obtener el plan FREE:", error);
  }
  return FREE_SUBSCRIPTION_LIMIT;
}

const isAmazonUrl = (url) => {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes("amzn.") || parsed.hostname.includes("a.co")) {
      return true;
    }
    const supportedDomains = [
      "amazon.es", "amazon.com", "amazon.fr", "amazon.de", "amazon.it", "amazon.co.uk",
    ];
    const isSupportedDomain = supportedDomains.some((domain) => parsed.hostname.endsWith(domain));
    const isProductPath = /\/(dp|gp\/product)\//.test(parsed.pathname);
    return isSupportedDomain && isProductPath;
  } catch {
    return false;
  }
};

function App({ initialAuthIntent = null }) {
  const [isUserDataLoaded, setIsUserDataLoaded] = useState(false);
  const [userData, setUserData] = useState(null);
  const [user, setUser] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [allowedLimit, setAllowedLimit] = useState(FREE_SUBSCRIPTION_LIMIT);
  const [activeCount, setActiveCount] = useState(0);
  const [urls, setUrls] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [urlInput, setUrlInput] = useState(() => {
    try { return sessionStorage.getItem('alertProductDraft') || ''; } catch { return ''; }
  });
  const [emailSentTo, setEmailSentTo] = useState('');
  const [urlPrellenada, setUrlPrellenada] = useState(false);
  const [sugerirBoton, setSugerirBoton] = useState(false);
  const [slugsHistorico, setSlugsHistorico] = useState({});
  const scrollHechoRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [loadingDeleteId, setLoadingDeleteId] = useState(null);
  const [hasConsented, setHasConsented] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [newsletterChoice, setNewsletterChoice] = useState(null);
  const [authLandingIntent, setAuthLandingIntent] = useState(initialAuthIntent);
  const [shouldShowAuthModal, setShouldShowAuthModal] = useState(() => initialAuthIntent !== null);

  const isAppLoading = !isAuthChecked || !isUserDataLoaded;
  const shouldRenderPublicLanding = !user && isAuthChecked && isUserDataLoaded;
  useEffect(() => {
    try {
      if (urlInput) sessionStorage.setItem('alertProductDraft', urlInput);
      else sessionStorage.removeItem('alertProductDraft');
    } catch { /* The form also works when browser storage is unavailable. */ }
  }, [urlInput]);

  useEffect(() => {
    if (location.state?.emailConfirmed) {
      showMessage("¡Tu email ha sido confirmado!");
      window.history.replaceState({}, document.title);
    }
    // Se llega aquí tras añadir un producto a mano: es el momento en que la
    // fricción de copiar y pegar el enlace está más fresca, así que es cuando
    // mejor se entiende la propuesta del botón de navegador.
    if (location.state?.productoAnadido) {
      setSugerirBoton(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Prerrellena el formulario al llegar con ?url= (bookmarklet de Amazon, o un
  // enlace compartido). Después limpia el parámetro para que no quede en el
  // historial ni se reenvíe al recargar. Al desaparecer el parámetro, la segunda
  // ejecución del efecto sale por el return temprano: no hay bucle.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlParam = params.get("url");
    if (!urlParam) return;

    if (isAmazonUrl(urlParam)) {
      setUrlInput(urlParam);
      setUrlPrellenada(true);
    } else {
      toast.error("Ese enlace no parece un producto de Amazon.");
    }

    params.delete("url");
    const resto = params.toString();
    navigate(`${location.pathname}${resto ? `?${resto}` : ""}`, { replace: true });
  }, [location.search, location.pathname, navigate]);

  // Al llegar desde el bookmarklet el campo queda muy por debajo del pliegue,
  // sobre todo sin sesión iniciada: primero va el Hero y toda la landing, así
  // que el usuario ve la portada y cree que no ha pasado nada. Se espera a que
  // la app termine de cargar para que el campo exista ya en el DOM.
  useEffect(() => {
    // La guarda es una ref y no estado: cambiar estado aquí alteraría las
    // dependencias del efecto, dispararía el cleanup y mataría el intervalo
    // antes del primer reintento.
    if (!urlPrellenada || isAppLoading || scrollHechoRef.current) return;
    scrollHechoRef.current = true;

    // El Hero lleva un vídeo con póster que carga después del primer render y
    // empuja el contenido hacia abajo: un unico scroll se queda corto y el
    // campo acaba fuera de pantalla. Se reintenta hasta que el layout se
    // estabiliza; cuando ya está centrado, scrollIntoView no mueve nada.
    let intentos = 0;
    const reintento = setInterval(() => {
      const campo =
        document.getElementById("product-url") || document.getElementById("url-input");
      if (campo) campo.scrollIntoView({ behavior: "smooth", block: "center" });
      if (++intentos >= 5) clearInterval(reintento);
    }, 400);

    showMessage(
      user
        ? "Producto listo: pulsa «Guardar alerta» para vigilarlo."
        : "Producto listo: añade tu email y creamos la alerta."
    );

    return () => clearInterval(reintento);
  }, [urlPrellenada, isAppLoading, user]);

  useEffect(() => {
    setAuthLandingIntent(initialAuthIntent);
    setShouldShowAuthModal(initialAuthIntent !== null);
  }, [initialAuthIntent]);

  // Índice de fichas de histórico, generado en el build. Se consulta para saber
  // qué productos seguidos tienen ya página y enlazarla desde su tarjeta. Es un
  // fichero mínimo (id y slug); si falla, simplemente no se muestra el enlace.
  useEffect(() => {
    if (!user) return;
    let cancelado = false;
    fetch("/precio/indice.json")
      .then((r) => (r.ok ? r.json() : []))
      .then((lista) => {
        if (cancelado || !Array.isArray(lista)) return;
        setSlugsHistorico(Object.fromEntries(lista.map(({ id, slug }) => [id, slug])));
      })
      .catch(() => {});
    return () => {
      cancelado = true;
    };
  }, [user]);

  useEffect(() => {
    if (!authLandingIntent) return;
    const titleMap = {
      login: "Iniciar sesión | Avísame si baja",
      signup: "Crear cuenta gratis | Avísame si baja",
    };
    const descriptionMap = {
      login: "Accede a tu cuenta de Avísame si baja para seguir tus productos favoritos de Amazon.",
      signup: "Crea tu cuenta gratuita en Avísame si baja y empieza a recibir avisos.",
    };
    const previousTitle = document.title;
    const metaDescription = document.querySelector('meta[name="description"]');
    const previousDescription = metaDescription?.getAttribute("content") ?? null;
    const nextTitle = titleMap[authLandingIntent] ?? previousTitle;
    const nextDescription = descriptionMap[authLandingIntent];

    document.title = nextTitle;
    if (metaDescription && nextDescription) {
      metaDescription.setAttribute("content", nextDescription);
    }

    if (window.gtag) {
      window.gtag("event", "auth_landing_view", {
        landing_type: authLandingIntent,
        page_path: location.pathname,
      });
    }

    return () => {
      document.title = previousTitle;
      if (metaDescription && previousDescription !== null) {
        metaDescription.setAttribute("content", previousDescription);
      }
    };
  }, [authLandingIntent, location.pathname]);

  useEffect(() => {
    if (user && authLandingIntent && location.pathname !== "/") {
      navigate("/", { replace: true });
    }
  }, [authLandingIntent, location.pathname, navigate, user]);

  useEffect(() => {
    let cleanupProductListeners = null;
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setIsUserDataLoaded(false);
      if (cleanupProductListeners) {
        cleanupProductListeners();
        cleanupProductListeners = null;
      }
      const freeLimit = await getFreePlanLimit();

      try {
        if (firebaseUser) {
          setUser(firebaseUser);
          setEmailInput(firebaseUser.email);
          const pendingUrl = localStorage.getItem("pendingUrlToTrack");
          if (pendingUrl) {
            try {
              const resultado = await callApiTrack(pendingUrl, firebaseUser.email);
              if (!resultado.error) {
                showMessage("Alerta creada. Ya estamos siguiendo tu producto.", "success");
                setUrlInput('');
                if (window.gtag) {
                  window.gtag("event", "conversion", {
                    send_to: "AW-970875118/producto_anadido",
                    value: 1.0,
                    currency: "EUR",
                  });
                }
              } else {
                showMessage(`Error al añadir producto: ${resultado.error}`, "error");
              }
            } catch (e) {
              console.error("Error al añadir URL tras confirmar email:", e);
            } finally {
              localStorage.removeItem("pendingUrlToTrack");
            }
          }

          const userRef = doc(db, "users", firebaseUser.uid);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            await updateDoc(userRef, { lastLogin: serverTimestamp() });
          } else {
            await setDoc(userRef, {
              email: firebaseUser.email,
              createdAt: serverTimestamp(),
              lastLogin: serverTimestamp(),
              isSubscribed: false,
              subscriptionLimit: FREE_SUBSCRIPTION_LIMIT,
              followedProducts: [],
            });
          }

          const userDocSnap = await getDoc(doc(db, "users", firebaseUser.uid));
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            setUserData(userData);
            setIsSubscribed(userData.isSubscribed || false);
            setAllowedLimit(userData.subscriptionLimit || FREE_SUBSCRIPTION_LIMIT);
            setNewsletterChoice(userData.newsletterPreference ?? null);

            if (window.gtag) {
              window.gtag("event", "login", {
                method: firebaseUser.providerData[0]?.providerId === "google.com" ? "google" : "email",
              });
              window.gtag("event", "conversion", {
                send_to: "AW-970875118/login_conversion",
                value: 1.0,
                currency: "EUR",
              });
            }

            const active = (userData.followedProducts || []).filter((fp) => fp.active).length;
            setActiveCount(active);

            const followedProductsIds = userData.followedProducts?.map((fp) => fp.productId) || [];
            if (followedProductsIds.length > 0) {
              const batchSize = 10;
              const batches = [];
              for (let i = 0; i < followedProductsIds.length; i += batchSize) {
                const batchIds = followedProductsIds.slice(i, i + batchSize);
                const productsRef = collection(db, "products");
                const q = query(productsRef, where("__name__", "in", batchIds));
                batches.push(q);
              }

              const unsubscribes = batches.map((q) =>
                onSnapshot(q, (snapshot) => {
                  const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
                  setUrls((prevUrls) => {
                    const mergedUrls = [
                      ...prevUrls.filter((url) => !data.some((newUrl) => newUrl.id === url.id)),
                      ...data,
                    ];
                    return mergedUrls;
                  });
                })
              );

              cleanupProductListeners = () => unsubscribes.forEach((unsub) => unsub());
              setIsUserDataLoaded(true);
            } else {
              setUrls([]);
              setIsUserDataLoaded(true);
            }
          } else {
            // Fallback user init logic
            setIsSubscribed(false);
            setAllowedLimit(freeLimit);
            setActiveCount(0);
            setUrls([]);
            setIsUserDataLoaded(true);
            if (!localStorage.getItem("conversionLogged")) {
              if (window.gtag) {
                window.gtag("event", "conversion", {
                  send_to: "AW-970875118/IaTcCOr_xwgQ7sH5zgM",
                  value: 1.0,
                  currency: "EUR",
                  transaction_id: "",
                });
              }
              localStorage.setItem("conversionLogged", "true");
            }
          }
        } else {
          setUser(null);
          setIsSubscribed(false);
          setAllowedLimit(freeLimit);
          setActiveCount(0);
          setUrls([]);
          setIsUserDataLoaded(true);
        }
      } catch (error) {
        console.error("Error al preparar los datos del usuario:", error);
        showMessage("Hubo un problema al cargar tu experiencia.", "error");
        setUser(null);
        setIsSubscribed(false);
        setAllowedLimit(freeLimit);
        setActiveCount(0);
        setUrls([]);
        setIsUserDataLoaded(true);
      } finally {
        setIsAuthChecked(true);
      }
    });

    return () => {
      if (cleanupProductListeners) cleanupProductListeners();
      unsubscribe();
    };
  }, []);

  const [premiumInterestSent, setPremiumInterestSent] = useState(false);

  const handleInterestInMoreUrls = async () => {
    if (!user) {
      showMessage("Debes iniciar sesión para registrar tu interés.", "warn");
      return;
    }
    try {
      const idToken = await auth.currentUser.getIdToken();
      await fetch(
        "https://europe-west1-avisamesibaja-be02c.cloudfunctions.net/registerPremiumInterest",
        {
          method: "POST",
          headers: { Authorization: `Bearer ${idToken}` },
        }
      );
      setPremiumInterestSent(true);
      showMessage("¡Gracias! Te avisaremos en cuanto esté disponible.", "success");
    } catch (error) {
      console.error("Error al registrar interés:", error);
      showMessage("Error al registrar el interés.", "error");
    }
  };

  async function callApiTrack(urlInput, emailInput) {
    const idToken = await auth.currentUser.getIdToken();
    const response = await fetch(
      "https://europe-west1-avisamesibaja-be02c.cloudfunctions.net/apiTrack",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          url: urlInput,
          email: emailInput,
          userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      }
    );
    const result = await response.json();
    return result;
  }

  const showMessage = (msg, type = "info") => {
    if (type === "success") toast.success(msg);
    else if (type === "error") toast.error(msg);
    else if (type === "warn") toast.warn(msg);
    else toast.info(msg);
  };

  const handleAuthenticatedSubmit = async (e) => {
    e.preventDefault();
    if (!urlInput) {
      showMessage("Por favor, completa la URL.");
      return;
    }
    if (!isAmazonUrl(urlInput)) {
      showMessage("Solo se permiten URLs de Amazon.", "warn");
    }
    if (urls.some((item) => item.url === urlInput)) {
      showMessage("Ya tienes añadida esta URL.");
      return;
    }

    const userDocSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
    let active = 0;
    if (userDocSnap.exists()) {
      const userData = userDocSnap.data();
      active = (userData.followedProducts || []).filter(fp => fp.active).length;
      setAllowedLimit(userData.subscriptionLimit || FREE_SUBSCRIPTION_LIMIT);
      setActiveCount(active);
    }

    if (active >= allowedLimit) {
      showMessage(`Has alcanzado el límite de URLs (${allowedLimit}).`, "error");
      return;
    }

    setIsLoading(true);
    try {
      const resultado = await callApiTrack(urlInput, emailInput);
      if (resultado.error) {
        showMessage(`Error: ${resultado.error}`, "error");
      } else {
        showMessage("Producto añadido correctamente.", "success");
        setUrlInput("");
        if (window.gtag) {
          window.gtag('event', 'conversion', {
            send_to: 'AW-970875118/producto_anadido',
            value: 1.0,
            currency: 'EUR'
          });
        }
        // Eliminado redirección para UX más fluida
        /* setTimeout(() => navigate('/producto-anadido'), 1500); */

        const userDocSnapRefresh = await getDoc(doc(db, "users", auth.currentUser.uid));
        if (userDocSnapRefresh.exists()) {
          const refreshedUserData = userDocSnapRefresh.data();
          setUserData(refreshedUserData);
          const active = (refreshedUserData.followedProducts || []).filter(fp => fp.active).length;
          setActiveCount(active);
          await refreshUserProducts(setUrls);
        }
      }
    } catch (error) {
      console.error("Error en el proceso autenticado:", error);
      showMessage(`Error al añadir el producto: ${error.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnonymousSubmit = async (e) => {
    e.preventDefault();
    if (!urlInput || !emailInput) {
      showMessage("Por favor, completa todos los campos.", "warn");
      return;
    }
    if (!hasConsented) {
      showMessage("Debes aceptar la política de privacidad.", "warn");
      return;
    }
    if (!isAmazonUrl(urlInput)) {
      showMessage("Introduce el enlace de un producto de Amazon.", "warn");
      return;
    }
    setIsLoading(true);
    try {
      await sendSignInLinkToEmail(auth, emailInput, actionCodeSettings);
      window.localStorage.setItem("emailForSignIn", emailInput);
      window.localStorage.setItem("pendingUrlToTrack", urlInput);
      setEmailSentTo(emailInput);
      if (window.gtag) {
        window.gtag('event', 'sign_up', { method: 'email' });
      }
      showMessage("📩 Te hemos enviado un enlace a tu correo.", "success");
    } catch (error) {
      console.error("Error en el proceso anónimo:", error);
      showMessage(`Error al enviar el email: ${error.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isAmazonUrl(urlInput)) {
      showMessage("Solo se permiten URLs de productos de Amazon.", "warn");
      return;
    }
    if (!user) {
      handleAnonymousSubmit(e);
    } else {
      handleAuthenticatedSubmit(e);
    }
  };

  async function refreshUserProducts(setUrlsCallback) {
    try {
      const userDocSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        const followedProductIds = userData.followedProducts?.map(p => p.productId) || [];
        if (followedProductIds.length === 0) {
          setUrlsCallback([]);
          return;
        }
        const batchSize = 10;
        const batches = [];
        for (let i = 0; i < followedProductIds.length; i += batchSize) {
          const batchIds = followedProductIds.slice(i, i + batchSize);
          const productsRef = collection(db, "products");
          const q = query(productsRef, where("__name__", "in", batchIds));
          batches.push(q);
        }
        const allDocs = [];
        for (const q of batches) {
          const snapshot = await getDocs(q);
          snapshot.forEach(doc => allDocs.push({ id: doc.id, ...doc.data() }));
        }
        allDocs.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(0);
          const dateB = b.createdAt?.toDate?.() || new Date(0);
          return dateB - dateA;
        });
        setUrlsCallback(allDocs);
      }
    } catch (error) {
      console.error('Error refreshing user products:', error);
    }
  }

  useEffect(() => {
    if (user) {
      const userRef = doc(db, "users", user.uid);
      const unsubscribeUser = onSnapshot(userRef, async (snapshot) => {
        if (!snapshot.exists()) return;
        const data = snapshot.data();
        setUserData(data);
        const limit = data?.subscriptionLimit || FREE_SUBSCRIPTION_LIMIT;
        const isSubscribedFlag = data?.isSubscribed || false;
        const followed = data.followedProducts || [];

        const updated = followed.map((fp, index) => ({ ...fp, active: index < limit }));
        const isDifferent = followed.some((fp, index) => fp.active !== (index < limit));

        if (isDifferent) {
          await updateDoc(userRef, { followedProducts: updated });
          setActiveCount(updated.filter(fp => fp.active).length);
        } else {
          setActiveCount(followed.filter(fp => fp.active).length);
        }
        setAllowedLimit(limit);
        setIsSubscribed(isSubscribedFlag);
      });
      return () => unsubscribeUser();
    }
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("refresh") === "true") {
      const newUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      window.location.reload();
    }
  }, [location.search]);

  async function toggleProductAlert(productId, currentAlertState) {
    if (!user) {
      showMessage("Debes estar autenticado para cambiar la alerta.", "error");
      return;
    }
    try {
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();
      const followedProducts = userData.followedProducts || [];
      const nextAlertState = !currentAlertState;
      const updatedProducts = followedProducts.map(fp => {
        if (fp.productId !== productId) return fp;

        const updated = { ...fp, alertEnabled: nextAlertState };
        if (nextAlertState) {
          delete updated.autoPaused;
          delete updated.autoPauseReason;
          delete updated.autoPausedAt;
        }
        return updated;
      });
      await updateDoc(userRef, { followedProducts: updatedProducts });
      setUserData(prev => ({ ...prev, followedProducts: updatedProducts }));
      showMessage(`Alerta ${nextAlertState ? "activada" : "desactivada"} correctamente.`);
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      showMessage("Error al cambiar el estado de la alerta.", "error");
    }
  }

  async function handleDelete(productId) {
    if (!user) {
      showMessage("Debes estar autenticado para borrar.", "error");
      return;
    }
    setLoadingDeleteId(productId);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(
        "https://europe-west1-avisamesibaja-be02c.cloudfunctions.net/deleteProductIfUnused",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`,
          },
          body: JSON.stringify({ productId }),
        }
      );
      if (!res.ok) throw new Error(await res.text());
      showMessage("✅ Producto eliminado correctamente.");
      setUrls(prev => prev.filter(item => item.id !== productId));

      const userDocSnapRefresh = await getDoc(doc(db, "users", auth.currentUser.uid));
      if (userDocSnapRefresh.exists()) {
        const refreshedUserData = userDocSnapRefresh.data();
        setUserData(refreshedUserData);
        const active = (refreshedUserData.followedProducts || []).filter(fp => fp.active).length;
        setActiveCount(active);
      }
    } catch (err) {
      console.error("❌ Error al borrar producto:", err);
      showMessage(err.message || "Error al eliminar", "error");
    } finally {
      setLoadingDeleteId(null);
    }
  }

  const handleNewsletterChoice = async (choice) => {
    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        newsletterPreference: choice,
        newsletterDate: serverTimestamp()
      });
      setNewsletterChoice(choice);
      if (choice) {
        showMessage("✅ ¡Perfecto! Te enviaremos las mejores ofertas.", "success");
      } else {
        showMessage("👍 Entendido. Solo recibirás alertas de precio.", "info");
      }
    } catch (error) {
      console.error("Error guardando preferencia:", error);
      showMessage("❌ Error al guardar preferencia.", "error");
    }
  };

  const handleGoogleLogin = async () => {
    if (isLoading) return;
    if (!hasConsented) {
      showMessage("Debes aceptar la Política de Privacidad.", "error");
      return;
    }
    if (!isAmazonUrl(urlInput)) {
      showMessage("Introduce el enlace de un producto de Amazon.", "warn");
      return;
    }
    setIsLoading(true);
    try {
      const { GoogleAuthProvider, signInWithPopup } = await import("firebase/auth");
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
      localStorage.setItem('pendingUrlToTrack', urlInput);
      await signInWithPopup(auth, provider);

      if (window.gtag) {
        window.gtag("event", "sign_up", { method: "google" });
      }
      showMessage("Has iniciado sesión correctamente.", "success");
    } catch (error) {
      localStorage.removeItem('pendingUrlToTrack');
      console.error("Error en login con Google:", error);
      showMessage("No se ha completado el acceso con Google. Puedes volver a intentarlo o usar tu email.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="app-shell">
      <Navbar
        user={user}
        setUser={setUser}
        defaultShowAuth={shouldShowAuthModal}
        onAuthModalClose={() => {
          setShouldShowAuthModal(false);
          if (authLandingIntent) {
            setAuthLandingIntent(null);
            if (location.pathname !== "/") {
              navigate("/", { replace: true });
            }
          }
        }}
        onAuthModalOpen={() => setShouldShowAuthModal(true)}
      />

      {shouldRenderPublicLanding && authLandingIntent && (
        <div style={{
          margin: "1.5rem auto 0",
          maxWidth: "960px",
          padding: "1rem 1.5rem",
          background: "#fff4d6",
          border: "1px solid #f0c36d",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        }}>
          <h2 style={{ marginBottom: "0.5rem" }}>
            {authLandingIntent === "login"
              ? "Inicia sesión en Avísame Si Baja"
              : "Crea tu cuenta gratuita en Avísame Si Baja"}
          </h2>
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            {authLandingIntent === "login"
              ? "Completa el acceso utilizando tu cuenta de Google o solicita un enlace mágico por email."
              : "Regístrate en segundos con Google o recibe un enlace mágico por email para empezar a seguir productos."}
          </p>
        </div>
      )}


      {isAppLoading ? (
        <div className="app-loading-screen">
          <Spinner size={24} />
          <p>{user ? "Preparando tus alertas..." : "Cargando..."}</p>
        </div>
      ) : (
        <div className="main-content">
          {shouldRenderPublicLanding && (
            <LandingPage
              hasConsented={hasConsented}
              setHasConsented={setHasConsented}
              handleGoogleLogin={handleGoogleLogin}
              handleSubmit={handleSubmit}
              emailInput={emailInput}
              setEmailInput={setEmailInput}
              urlInput={urlInput}
              setUrlInput={setUrlInput}
              isLoading={isLoading}
              emailSentTo={emailSentTo}
              resetEmailSent={() => setEmailSentTo('')}
              allowedLimit={allowedLimit}
              isAmazonUrl={isAmazonUrl}
            />
          )}

          {user && (
            <>
              <AlertDashboard
                activeCount={activeCount} allowedLimit={allowedLimit} urlInput={urlInput}
                urls={urls} userData={userData} user={user}
                loadingDeleteId={loadingDeleteId} handleDelete={handleDelete}
                toggleProductAlert={toggleProductAlert} appendAffiliateTag={appendAffiliateTag}
                slugsHistorico={slugsHistorico}
              >
              <ProductForm
                urlInput={urlInput}
                setUrlInput={setUrlInput}
                handleSubmit={handleSubmit}
                isLoading={isLoading}
                user={user}
              />
              </AlertDashboard>

              {sugerirBoton && (
                <div className="sugerencia-boton">
                  <p>
                    ✅ Alerta creada. Para la próxima,{" "}
                    <a href="/boton-navegador">añade el botón a tu navegador</a> y vigila
                    cualquier producto de Amazon con un solo clic, sin copiar enlaces.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSugerirBoton(false)}
                    aria-label="Cerrar sugerencia"
                  >
                    ✕
                  </button>
                </div>
              )}

              {activeCount >= allowedLimit && (
                <div className="limit-notice">
                  <p style={{ fontSize: "1.05em", marginBottom: "0.4rem" }}>
                    ¿Necesitas seguir más productos?
                  </p>
                  <p style={{ fontSize: "0.9em", color: "#2a4a6a", marginBottom: "1.1rem", fontWeight: 400 }}>
                    Déjanos tu interés y te avisaremos cuando haya una opción para ampliar tu límite.
                  </p>
                  {premiumInterestSent ? (
                    <p style={{ fontWeight: 600, color: "#1a6b3a" }}>
                      ✅ ¡Anotado! Te avisaremos en cuanto esté disponible.
                    </p>
                  ) : (
                    <button onClick={handleInterestInMoreUrls} className="limit-notice-button">
                      ⭐ Me interesa el plan Premium
                    </button>
                  )}
                </div>
              )}

              <SocialLinks title="🔥 ¿Quieres ver más ofertas y chollos? ¡Síguenos!" />

              <NewsletterInvite
                newsletterChoice={newsletterChoice}
                handleNewsletterChoice={handleNewsletterChoice}
              />


            </>
          )}

          <Footer />
        </div>
      )}

      <ToastContainer position="top-center" autoClose={4000} />
      <CookiesBanner />
    </div>
  );
}

export async function deleteUserData(uid) {
  const userRef = doc(db, "users", uid);
  const userSnap = await getDoc(userRef);
  if (!userSnap.exists()) return;

  const userData = userSnap.data();
  const followedProducts = userData.followedProducts || [];

  for (const { productId } of followedProducts) {
    const productRef = doc(db, "products", productId);
    const productSnap = await getDoc(productRef);

    if (productSnap.exists) {
      const q = query(
        collection(db, "users"),
        where("followedProducts", "array-contains", { productId, active: true })
      );
      const usersFollowing = await getDocs(q);
      if (usersFollowing.size <= 1) {
        await deleteDoc(productRef);
      } else {
        await updateDoc(userRef, {
          followedProducts: arrayRemove({ productId, active: true }),
        });
      }
    }
  }
  await deleteDoc(userRef);
}

export default App;
